import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTaskStore } from '../stores/pmTaskStore';
import { saveOptimistic } from '../stores/createProjectItemStore';
import { Input, Textarea, CustomSelect, Label, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError } from '@redpanda/ui';
import { toast } from 'react-hot-toast';
import { createTask, updateTask, getTimeEntries } from '../services/api';

import { toDateInput, toISOString, getTodayDateInput } from '../utils/dateUtils';
import { notifyDataChanged } from '../utils/dataEvents';
import { PMTask, ChecklistItem } from '../types/pm';
import { useRequirementStore } from '../stores/pmRequirementStore';
import TaskChecklist from './TaskChecklist';
import TaskActivityPanel from './TaskActivityPanel';
import TimeEntryDialog from './TimeEntryDialog';
import { formatHours } from './TimeEntryTable';
import { getPriorityOptions, getTaskStatusOptions } from '../utils/constants';
import { useMembers, withCurrentValue } from '../stores/lookupStore';
import { UserContext } from '../context/UserContext';
import { useNumberInput, useUnsavedChangesWarning } from '@redpanda/utils';

interface TaskDialogProps {
  isOpen: boolean;
  onClose: (shouldRefresh?: boolean) => void;
  projectId: string;
  task?: PMTask;
  milestones?: Array<{ id: string; name: string }>;
}

interface FormData {
  title: string;
  description?: string;
  milestoneId?: string;
  requirementId?: string;
  checklist?: ChecklistItem[];
  assignedTo?: string;
  estimatedHours?: number;
  actualHours?: number;
  dueDate?: string;
  priority: string;
  status: string;
}

const TaskDialog: React.FC<TaskDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  task,
  milestones = [],
}) => {
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, reset, watch } = useForm<FormData>();

  const actualHandleClose = () => {
    reset();
    onClose(false);
  };

  const {
    handleClose: handleCloseWithWarning,
    showConfirm,
    handleConfirmClose,
    handleCancelClose,
  } = useUnsavedChangesWarning(isDirty, actualHandleClose);

  const { t } = useTranslation(['translation', 'pages']);
  const estimatedHoursInput = useNumberInput('decimal');
  const actualHoursInput = useNumberInput('decimal');

  const isEditMode = !!task;
  const savedTask = isEditMode && !task.id.startsWith('temp-'); // 樂觀新增、還沒存進資料庫的任務不能記工時

  // 工時記錄：有記錄時實際工時＝加總（唯讀，後端也不接受手動值）；null＝還沒有記錄，可以手動填
  const [loggedHours, setLoggedHours] = useState<number | null>(null);
  const [loggingTime, setLoggingTime] = useState(false);
  const loadLoggedHours = () => {
    if (!savedTask) return;
    getTimeEntries({ taskId: task.id })
      .then(({ data }) => {
        const sum = data.length ? Math.round(data.reduce((acc, e) => acc + e.hours, 0) * 100) / 100 : null;
        setLoggedHours(sum);
        if (sum !== null) setValue('actualHours', sum); // 不標記 dirty：不是使用者改的
      })
      .catch(error => console.error('Failed to load time entries:', error));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isOpen) loadLoggedHours(); else setLoggedHours(null); }, [isOpen, task?.id]);

  // 監聽表單值變化
  const watchedPriority = watch('priority', 'MEDIUM');
  const watchedStatus = watch('status', 'PENDING');
  const watchedMilestoneId = watch('milestoneId', '');
  const watchedAssignee = watch('assignedTo', '');
  const watchedRequirementId = watch('requirementId', '');
  const watchedChecklist = watch('checklist') || [];
  const requirements = useRequirementStore(state => state.items).filter(r => r.projectId === projectId);

  // 負責人：從組織成員選（存 userId，「我的任務」依此篩選；主流 PM 工具同做法）
  const members = useMembers();
  const currentUserId = useContext(UserContext)?.user?.id;
  const assigneeOptions = withCurrentValue(
    [{ value: '', label: t('pmTasks.unassigned', { ns: 'pages' }) }, ...members.map(m => ({ value: m.id, label: m.name || m.email }))],
    watchedAssignee
  );
  const setAssignee = (value: string) => setValue('assignedTo', value, { shouldDirty: true });

  // 設置表單初始值（useLayoutEffect：繪製前帶入，編輯時不會先閃一格空白欄位）
  useLayoutEffect(() => {
    if (isOpen) {
      const today = getTodayDateInput();
      if (task) {
        // 編輯模式：使用 reset 設置初始值，避免標記為 dirty
        reset({
          title: task.title,
          description: task.description || '',
          milestoneId: task.milestoneId || '',
          requirementId: task.requirementId || '',
          checklist: task.checklist || [],
          assignedTo: task.assignedTo || '',
          estimatedHours: task.estimatedHours,
          actualHours: task.actualHours,
          dueDate: task.dueDate ? toDateInput(task.dueDate) : '',
          priority: task.priority,
          status: task.status,
        });
      } else {
        // 新增模式：使用 reset 設置預設值
        reset({
          title: '',
          description: '',
          milestoneId: '',
          requirementId: '',
          checklist: [],
          assignedTo: '',
          estimatedHours: undefined,
          actualHours: undefined,
          dueDate: today,
          priority: 'MEDIUM',
          status: 'PENDING',
        });
      }
    }
  }, [isOpen, task, reset]);

  const onSubmit = async (data: FormData) => {
    const payload = {
      title: data.title.trim(),
      description: data.description?.trim() || null,
      milestoneId: data.milestoneId || null,
      requirementId: data.requirementId || null,
      checklist: data.checklist?.length ? data.checklist : null,
      assignedTo: data.assignedTo?.trim() || null,
      estimatedHours: data.estimatedHours ? parseFloat(data.estimatedHours.toString()) : null,
      actualHours: data.actualHours ? parseFloat(data.actualHours.toString()) : null,
      dueDate: data.dueDate ? toISOString(data.dueDate) : null,
      priority: data.priority,
      status: data.status,
    };

    const now = new Date().toISOString();
    const draft = (isEditMode
      ? { ...task, ...payload }
      : { id: `temp-${Date.now()}`, projectId, createdAt: now, updatedAt: now, ...payload }) as PMTask;
    actualHandleClose();

    try {
      await saveOptimistic(useTaskStore, {
        original: task,
        draft,
        request: () => (isEditMode ? updateTask(task.id, payload) : createTask(projectId, payload)),
      });
      toast.success(t(isEditMode ? 'common.messages.updateSuccess' : 'common.messages.createSuccess'));
      notifyDataChanged('tasks');
    } catch (error: any) {
      toast.error(error.response?.data?.error || t(isEditMode ? 'pmTasks.updateError' : 'pmTasks.createError', { ns: 'pages' }));
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <DialogShell onClose={handleCloseWithWarning} className="max-w-2xl">
        <DialogHeader title={isEditMode ? t('pmTasks.editTitle', { ns: 'pages' }) || 'Edit Task' : t('pmTasks.addTitle', { ns: 'pages' }) || 'Add Task'} onClose={handleCloseWithWarning} closeLabel={t('common.actions.close')} />

        {/* 對話框內容 */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-5">
            {/* 任務標題 */}
            <FormFieldWithError
              label={t('pmTasks.fields.title', { ns: 'pages' }) || 'Title'}
              required={true}
              error={errors.title?.message}
              htmlFor="title"
            >
              <Input
                type="text"
                id="title"
                {...register('title', {
                  required: t('pmTasks.titleRequired', { ns: 'pages' }) || 'Title is required',
                  maxLength: { value: 200, message: t('pmTasks.validation.titleMaxLength', { ns: 'pages' }) }
                })}
                variant={errors.title ? "error" : "default"}
              />
            </FormFieldWithError>

            {/* 任務描述 */}
            <FormFieldWithError
              label={t('pmTasks.fields.description', { ns: 'pages' }) || 'Description'}
              error={errors.description?.message}
              htmlFor="description"
            >
              <Textarea
                id="description"
                {...register('description', {
                  maxLength: { value: 1000, message: t('pmTasks.validation.descriptionMaxLength', { ns: 'pages' }) }
                })}
                rows={3}
                variant={errors.description ? "error" : "default"}
              />
            </FormFieldWithError>

            {/* 關聯里程碑＋實作的需求 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="milestoneId">{t('pmTasks.fields.milestone', { ns: 'pages' }) || 'Milestone'}</Label>
              <CustomSelect
                id="milestoneId"
                {...register('milestoneId')}
                value={watchedMilestoneId}
                onChange={(value) => setValue('milestoneId', value)}
                placeholder={t('pmTasks.selectMilestone', { ns: 'pages' }) || 'Select milestone (optional)'}
                options={[
                  { value: '', label: t('pmTasks.noMilestone', { ns: 'pages' }) || 'No milestone' },
                  ...milestones.map(m => ({ value: m.id, label: m.name }))
                ]}
              />
            </div>
            <div>
              <Label htmlFor="requirementId">{t('pmTasks.fields.requirement', { ns: 'pages' })}</Label>
              <CustomSelect
                id="requirementId"
                value={watchedRequirementId || ''}
                onChange={(value) => setValue('requirementId', value, { shouldDirty: true })}
                options={[
                  { value: '', label: t('pmTasks.noRequirement', { ns: 'pages' }) },
                  ...requirements.map(r => ({ value: r.id, label: r.title })),
                ]}
                searchable={requirements.length > 8}
                searchPlaceholder={t('pmTasks.searchRequirement', { ns: 'pages' })}
              />
            </div>
            </div>

            {/* 指派給 + 到期日期 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-baseline justify-between">
                  <Label htmlFor="assignedTo">{t('pmTasks.fields.assignedTo', { ns: 'pages' })}</Label>
                  {currentUserId && watchedAssignee !== currentUserId && (
                    <button type="button" onClick={() => setAssignee(currentUserId)} className="text-[13px] text-accent hover:underline">
                      {t('pmTasks.assignToMe', { ns: 'pages' })}
                    </button>
                  )}
                </div>
                <CustomSelect
                  id="assignedTo"
                  value={watchedAssignee || ''}
                  onChange={setAssignee}
                  options={assigneeOptions}
                  searchable
                  searchPlaceholder={t('pmTasks.searchMember', { ns: 'pages' })}
                />
              </div>

              <FormFieldWithError
                label={t('pmTasks.fields.dueDate', { ns: 'pages' }) || 'Due Date'}
                error={errors.dueDate?.message}
                htmlFor="dueDate"
              >
                <Input
                  type="date"
                  id="dueDate"
                  {...register('dueDate')}
                />
              </FormFieldWithError>
            </div>

            {/* 預估工時 / 實際工時 / 優先級 / 狀態 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <FormFieldWithError
                label={t('pmTasks.fields.estimatedHoursWithUnit', { ns: 'pages' }) || '預估工時（h）'}
                error={errors.estimatedHours?.message}
                htmlFor="estimatedHours"
              >
                <Input
                  type="text"
                  id="estimatedHours"
                  {...estimatedHoursInput.bind(register('estimatedHours', estimatedHoursInput.validationRules))}
                  placeholder="0"
                />
              </FormFieldWithError>

              <FormFieldWithError
                label={t('pmTasks.fields.actualHours', { ns: 'pages' }) || '實際工時（h）'}
                error={errors.actualHours?.message}
                htmlFor="actualHours"
              >
                <Input
                  type="text"
                  id="actualHours"
                  {...actualHoursInput.bind(register('actualHours', actualHoursInput.validationRules))}
                  placeholder="0"
                  readOnly={loggedHours !== null}
                  className={loggedHours !== null ? 'bg-slate-100 dark:bg-slate-700 cursor-not-allowed' : ''}
                />
                {savedTask && (
                  <p className="mt-1 text-[13px] text-fg-secondary">
                    {loggedHours !== null && <>{t('pmTime.loggedOnTask', { ns: 'pages', hours: formatHours(loggedHours) })} · </>}
                    <button type="button" onClick={() => setLoggingTime(true)} className="text-accent hover:underline">
                      {t('pmTime.addButton', { ns: 'pages' })}
                    </button>
                  </p>
                )}
              </FormFieldWithError>

              <div>
                <Label htmlFor="priority">{t('pmTasks.fields.priority', { ns: 'pages' }) || 'Priority'}</Label>
                <CustomSelect
                  id="priority"
                  {...register('priority')}
                  value={watchedPriority}
                  onChange={(value) => setValue('priority', value)}
                  options={getPriorityOptions(t, 'pmTasks')}
                />
              </div>

              <div>
                <Label htmlFor="status">{t('pmTasks.fields.status', { ns: 'pages' }) || 'Status'}</Label>
                <CustomSelect
                  id="status"
                  {...register('status')}
                  value={watchedStatus}
                  onChange={(value) => setValue('status', value)}
                  options={getTaskStatusOptions(t)}
                />
              </div>
            </div>

            <TaskChecklist value={watchedChecklist} onChange={(items) => setValue('checklist', items, { shouldDirty: true })} />

            {isEditMode && !task.id.startsWith('temp-') && <TaskActivityPanel taskId={task.id} />}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={handleCloseWithWarning}
              className={dialogButtonClass.cancel}
            >
              {t('common.actions.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              className={dialogButtonClass.primary}
            >
              {isEditMode
                ? t('common.actions.update') || 'Update'
                : t('common.actions.create') || 'Create'}
            </button>
          </DialogFooter>
        </form>
    </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('components.modal.unsavedChanges.title')}
        message={t('components.modal.unsavedChanges.message')}
        confirmText={t('components.modal.unsavedChanges.confirmButton')}
        cancelText={t('components.modal.unsavedChanges.cancelButton')}
        t={t}
      />

      {loggingTime && (
        <TimeEntryDialog isOpen onClose={() => setLoggingTime(false)} projectId={projectId} taskId={task!.id} onSaved={loadLoggedHours} />
      )}
    </>
  );
};

export default TaskDialog;
